/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { useContentZoneContext } from "cms/components/context";
import { setFieldComponentProperties } from "data/store/reducers/model";
import { get } from "lodash";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useDebounce } from "react-use";
import { useLatestFieldPosition } from "./use-latest-field-position";
import { Logger } from "cms/utils/log/logger";

/**
 * Returns a useState tuple ([value, setValue]) that will save the value to
 * Redux when changed.
 * 
 * Note that this is for modifying a single field.
 * 
 * @param {string} propertyPath This path is relative to the component, not
 * the entire model.
 */
const useUpdateComponentProperty = (propertyPath) => {
    const dispatch = useDispatch();

    const { component } = useContentZoneContext();
    const { guid } = component;

    const fieldPositionRef = useLatestFieldPosition();

    const initialValue = useMemo(() => {
        const value = get(component, propertyPath);
        return value;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [value, setValue] = useState(initialValue);

    useDebounce(() => {
        if (initialValue !== value) {
            Logger.info(`[update-component-property] saving property '${propertyPath}' for component [${guid}]`,{ propertyPath, guid, value });
            
            /** @type [string, any] */
            const property = [propertyPath, value];
            const properties = [property];

            dispatch(setFieldComponentProperties({
                fieldPosition: fieldPositionRef.current,
                guid,
                properties
            }));
        }
    }, 500, [value]);

    /**
     * @type { [typeof value, typeof setValue] }
     */
    const valueTuple = [value, setValue];
    return valueTuple;
};

export { useUpdateComponentProperty };
